import type { AriaAttributes, MouseEventHandler, Ref } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { Icon, type IconName } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { match } from '@/utils/pattern-match-util'
import styles from './IconButton.module.css'

export interface IconButtonProps extends Omit<AriaAttributes, 'aria-label'> {
  /**
   * The icon to display. This should always be the first prop when using the component.
   */
  name: IconName

  /**
   * An accessible label.
   */
  label: string

  /**
   * ID for the button.
   */
  id?: string

  /**
   * Press handler.
   */
  onPress?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>

  /**
   * Ref to the underlying element.
   */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>

  /**
   * If specified, renders as a link to this path.
   */
  to?: string

  /**
   * If specified, renders as a link to this URL.
   */
  href?: string

  /**
   * When used with `to`, causes the route to be replaced rather than pushed.
   */
  replace?: boolean

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Display a spinner instead of the icon.
   */
  isPending?: boolean

  /**
   * The variant to use.
   */
  variant?: 'default' | 'ghost' | 'outline'

  /**
   * Whether to lay the button out inline with surrounding text.
   */
  inline?: boolean

  /**
   * Whether the icon takes the color of the surrounding text.
   */
  inheritColor?: boolean

  /**
   * Size of the icon in pixels.
   */
  size?: number

  /**
   * Rotation angle of the icon in degrees.
   */
  rotate?: number

  /**
   * Color of the icon.
   */
  color?: string

  /**
   * Class name to apply.
   */
  className?: string

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A button that displays an icon.
 */
export function IconButton({
  name,
  label,
  isDisabled,
  isPending,
  variant = 'default',
  inline,
  inheritColor,
  size,
  rotate,
  color,
  className,
  ...rest
}: IconButtonProps) {
  return (
    <RawButton
      isDisabled={isDisabled}
      isPending={isPending}
      aria-label={label}
      className={cx(
        styles.button,
        isPending && styles.loading,
        isDisabled && styles.disabled,
        inline && styles.inline,
        inheritColor && styles.inheritColor,
        match(variant, {
          default: null,
          ghost: styles.ghost,
          outline: styles.outline,
        }),
        className,
      )}
      {...rest}
    >
      {isPending ? (
        <Spinner size={24} />
      ) : (
        <Icon name={name} size={size} rotate={rotate} color={color} />
      )}
    </RawButton>
  )
}
